import { useEffect, useRef, useState } from "react";
import { isEmpty } from "lodash";
import { useLocation } from "react-router-dom";
import { formApi } from "api/form";
import RequestSubmitted from "components/common/RequestSubmitted";
import FlagHeader from "components/common/headers/FlagHeader";
import FormFooter from "components/common/footers/FormFooter";
import Layout from "components/common/layouts/Layout";
import { useApp } from "contexts/AppContext";
import { useForm } from "contexts/FormContext";
import { useModal } from "contexts/ModalContext";
import { useContentful } from "hooks/useContentful";
import { getAvaLoginUrl, isMyBusinessToolsHost } from "helpers/host";
import CnForm from "./CnForm";
import Loader from "components/common/loaders/Loader";

export default function CnFormContainer({
  contentfulId = "",
  successContentfulId = "",
}) {
  const {
    state: { locale, rep },
  } = useApp();
  const { form, market, overwriteAddress } = useForm();
  const { open } = useModal();

  const data = useContentful(contentfulId);
  const hasPrepopulated = useRef(false);

  const [success, setSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const location = useLocation();
  const [scrollCount, setScrollCount] = useState(0);

  useEffect(() => {
    if (hasPrepopulated.current || isEmpty(rep)) {
      return;
    }

    overwriteAddress({
      address: {
        ShipState: rep.ShipState || "",
        ShipCity: rep.ShipCity || "",
        ShipCounty: rep.ShipCounty || "",
        ShipStreet1: rep.ShipStreet1 || "",
        ShipStreet2: rep.ShipStreet2 || "",
      },
    });
    hasPrepopulated.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rep]);

  useEffect(() => {
    if (isSubmitting) {
      saveMigrationData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitting]);

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.slice(1);
      const anchor = document.getElementById(id);
      if (anchor) {
        anchor.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [scrollCount, location.hash]);

  if (!data) {
    return null;
  }

  const saveMigrationData = async () => {
    try {
      setIsLoading(true);
      const res = await formApi.saveMigrationDataCn({
        market,
        locale,
        rep,
        form,
      });

      if (res?.success === true) {
        setSuccess(true);
        return;
      }

      const message =
        typeof res === "string" ? res : res?.message || res?.error;

      if (message === "Contains illegal characters") {
        open({ type: "illegal-characters" });
      } else if (message) {
        open({
          type: "bank-error",
          props: { errorMessage: message },
          callback: () => {
            window.location.hash = "#address-section";
            setScrollCount(scrollCount + 1);
          },
        });
      }
      throw new Error(message || "Migration failed");
    } catch (err) {
      console.error("Migration failed", err);
      setIsSubmitting(false);
    } finally {
      setIsLoading(false);
    }
  };

  const onFormSubmit = () => {
    setIsSubmitting(true);
  };

  const { reference1 } = data?.fields;
  const resolvedSuccessContentfulId =
    successContentfulId || data?.fields?.reference7?.sys?.id;

  if (success) {
    return (
      <RequestSubmitted
        market={market}
        contentfulId={resolvedSuccessContentfulId}
        navUrl={getAvaLoginUrl()}
      />
    );
  }

  return (
    <Layout data={data} market={market}>
      {isLoading && <Loader active />}
      <FlagHeader data={reference1} market={market} />
      <CnForm data={data} market={market} />
      <FormFooter
        data={reference1}
        onFormSubmit={onFormSubmit}
        isSubmitting={isSubmitting}
        hideCancelLink={isMyBusinessToolsHost()}
      />
    </Layout>
  );
}
